// DEC-811: Backend credentials (server API keys, signing secrets, etc.) must
// NEVER appear in this file or anywhere else under client/src/. CRA inlines
// `process.env.REACT_APP_*` into the JavaScript bundle that ships to every
// browser — any secret-shaped REACT_APP_* reference here is a public-disclosure
// of that secret. All third-party API calls that require a server-grade
// credential (Texture/Device Controls, Anthropic, Green Button, etc.) MUST be
// proxied through the DECO backend, which holds the key server-side.
//
// A prebuild guard at client/scripts/check-no-secret-env-vars.js enforces
// this: any REACT_APP_*_KEY/_SECRET/_TOKEN/_PASSWORD that isn't in the
// explicit allowlist fails the build. The only current allowlisted entry is
// REACT_APP_GOOGLE_MAPS_API_KEY (referrer/IP-restricted, intentionally
// frontend-safe per DEC-754).
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// withCredentials is required for the deco_beta cookie to be stored/sent on
// cross-origin calls from decoenergy.net → api.decoenergy.net. Without it,
// browsers silently drop Set-Cookie on the response and never attach cookies
// on subsequent requests, regardless of server CORS config. The server side
// already opts in via cors({ credentials: true }) in server/app.js.
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and org context to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // DEC-443: Only attach org context when authenticated
    const activeOrg = localStorage.getItem('deco_active_org');
    // DEC-1009: don't clobber a caller-supplied X-Organization-Id. Some calls
    // (e.g. paymentsAPI's wallet/payout-account functions) must scope to a
    // SPECIFIC org — a site's own org, or whichever org's context a modal was
    // opened with — which is not necessarily whatever the user's global
    // "active org" happens to be right now. Explicit always wins; this only
    // fills in the header when the caller didn't already set one, which is
    // unchanged behavior for every existing call site.
    if (activeOrg && !config.headers['X-Organization-Id']) {
      config.headers['X-Organization-Id'] = activeOrg;
    }
  }
  return config;
});

// DEC-457: Global 401 response interceptor — auto-logout on expired tokens
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect on auth endpoints (login, register, verify)
      if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/verify')) {
        // DEC-1450: a held token that just 401'd is proof this browser had a
        // session. Record that BEFORE discarding the token — the beta gate
        // this redirect lands on has nothing else to go by, and without the
        // marker a returning consumer is shown the waitlist as if new.
        const { markReturningBrowser } = require('../utils/returningBrowser');
        if (localStorage.getItem('token')) markReturningBrowser();
        localStorage.removeItem('token');
        localStorage.removeItem('deco_active_org');
        // On a PUBLIC page (the PG&E direct enroll link, /terms, the upload
        // flow…) a stale token's 401 must only CLEAR the token — /login is a
        // gated route, so the old unconditional redirect yanked a public-page
        // visitor off the page and onto the beta WAITLIST (found 2026-09-04:
        // a May-era beta user with an expired token opening the pilot link
        // saw the waitlist before the enrollment page). The page they are on
        // works signed-out by definition; let it keep rendering.
        const { isPublicAuthPath } = require('../utils/publicPaths');
        if (!isPublicAuthPath(window.location.pathname)) {
          // DEC-1450: when the beta cookie is also gone, /login never mounts
          // (App renders the gate for every gated path), so the Login shim
          // that normally sets this flag never runs. Set it here so the gate
          // opens sign-in on arrival — forced, even past a prior dismissal —
          // and attributes the prompt to this redirect.
          try { sessionStorage.setItem('openAuthModal', 'true'); } catch (e) { /* storage blocked */ }
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  verifyCode: (data) => api.post('/auth/verify-code', data),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateAccountType: (userType) => api.put('/auth/account-type', { userType }),
  changePassword: (data) => api.put('/auth/change-password', data),
  // Unified auth flow (DEC-143)
  lookup: (data) => api.post('/auth/lookup', data),
  sendLoginCode: (data) => api.post('/auth/send-login-code', data),
  verifyLoginCode: (data) => api.post('/auth/verify-login-code', data),
  // dec804: consume a single-use magic-login link from a re-engagement email.
  // Explicit 15s timeout (the shared axios instance has none → waits forever):
  // a hung request on a mobile/in-app-browser network must surface a recoverable
  // error, not a permanent spinner. The /magic page also runs an 18s watchdog.
  consumeMagicLogin: (t) => api.post('/auth/magic-login/consume', { t }, { timeout: 15000 }),
  // DEC-1452: on-demand sign-in link. Pass { identifier } (an email) from the
  // sign-in sheet, or { t } (a spent/expired token) from the /magic page's
  // "Email me a new link". The response is uniform whether or not the address
  // has an account.
  requestMagicLogin: (data) => api.post('/auth/magic-login/request', data),
  sendOwnershipCode: (data) => api.post('/auth/send-ownership-code', data),
  verifyOwnershipCode: (data) => api.post('/auth/verify-ownership-code', data),
  completeRegistration: (data) => api.post('/auth/complete-registration', data),
};

// Sites API
export const sitesAPI = {
  create: (data) => api.post('/sites', data),
  getAll: () => api.get('/sites'),
  getById: (id) => api.get(`/sites/${id}`),
  update: (id, data) => api.put(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
  archive: (id) => api.put(`/sites/${id}/archive`, { archive: true }),
  unarchive: (id) => api.put(`/sites/${id}/archive`, { archive: false }),
  uploadImage: (id, formData) => api.post(`/sites/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// DEC-1148: beta Feedback widget submission. Authenticated-only; the server
// takes user_id from the session, never from this body.
export const feedbackAPI = {
  submit: (payload) => api.post('/bug-reports', payload),
};

// Devices API
export const devicesAPI = {
  add: (data) => api.post('/devices', data),
  getBySite: (siteId) => api.get(`/devices/site/${siteId}`),
  getCapabilitiesBySite: (siteId) => api.get(`/devices/site/${siteId}/capabilities`),
  update: (id, data) => api.put(`/devices/${id}`, data),
  updateOnboarding: (id, data) => api.put(`/devices/${id}/onboarding`, data),
  delete: (id) => api.delete(`/devices/${id}`),
};

// Agreement template uploads (admin-side via wizard) + presigned URL
// retrieval (consumer-side, when reading the agreement task).
export const agreementTemplatesAPI = {
  upload: async (file) => {
    // Use fetch() directly instead of axios. The shared `api` axios instance
    // sets `Content-Type: application/json` as a default header — when you
    // pass FormData, axios's auto-detection only kicks in if no Content-Type
    // is set. The instance default counts as "set", so axios sends
    // application/json with no multipart boundary, multer rejects the body,
    // and req.file is undefined ("No file uploaded"). fetch with no
    // Content-Type lets the browser set multipart/form-data WITH boundary
    // every time. Mirror axios's response shape for caller compatibility.
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('deco_active_org');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (orgId) headers['X-Organization-Id'] = orgId;
    const res = await fetch(`${API_URL}/agreement-templates/upload`, {
      method: 'POST',
      headers, // explicitly no Content-Type — browser sets it with boundary
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.error || data.detail || 'Upload failed');
      err.response = { data, status: res.status };
      throw err;
    }
    return { data };
  },
  getUrl: (documentId) => api.get(`/agreement-templates/${documentId}/url`),
};

// Operator Enrollment Packet API (DEC-646/647)
export const operatorPacketAPI = {
  getPacket: (enrollmentId) => api.get(`/operator/enrollments/${enrollmentId}/packet`),
  getDocumentUrl: (enrollmentId, docId) =>
    api.get(`/operator/enrollments/${enrollmentId}/documents/${docId}/url`),
  getAgreementUrl: (enrollmentId, acceptanceId) =>
    api.get(`/operator/enrollments/${enrollmentId}/agreements/${acceptanceId}/url`),
  patchTerritory: (enrollmentId, data) =>
    api.patch(`/operator/enrollments/${enrollmentId}/territory`, data),
  getTerritoryOptions: (enrollmentId) =>
    api.get(`/operator/enrollments/${enrollmentId}/territory/options`),
  // dec813: accept the ESTIMATED capacity on a strict program (operator
  // attestation; unblocks Approve). Required Idempotency-Key — one per click.
  acceptEstimatedCapacity: (enrollmentId, data = {}) =>
    api.post(`/operator/enrollments/${enrollmentId}/accept-estimated-capacity`, data, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),
};

// Programs API
export const programsAPI = {
  create: (data) => api.post('/programs', data),
  getPublic: () => api.get('/programs/public'),
  getAll: () => api.get('/programs'),
  getMyPrograms: () => api.get('/programs/my-programs'),
  getProgramById: (id) => api.get(`/programs/${id}`),
  getPublicProgramById: (id) => api.get(`/programs/public/${id}`),
  update: (id, data) => api.put(`/programs/${id}`, data),
  enroll: (data) => api.post('/programs/enroll', data),
  // Public-page traffic + enrollment funnel for the operator admin view.
  getTraffic: (id, days) => api.get(`/programs/${id}/traffic`, { params: days ? { days } : {} }),
  // DEC-934: embed widget admin (origin allowlist + snippet data).
  getEmbedOrigins: (id) => api.get(`/programs/${id}/embed-origins`),
  addEmbedOrigin: (id, origin) => api.post(`/programs/${id}/embed-origins`, { origin }),
  removeEmbedOrigin: (id, originId) => api.delete(`/programs/${id}/embed-origins/${originId}`),
  getEnrollments: (siteId) => api.get(`/programs/enrollments/${siteId}`),
  getPendingEnrollments: () => api.get('/programs/pending-enrollments'),
  getPendingDisenrollments: () => api.get('/programs/pending-disenrollments'),
  getActiveEnrollments: (programId) => api.get(`/programs/${programId}/active-enrollments`),
  // DEC-1242: nomination roster + export (JSON) and the delivery CSV (blob).
  getNominationRoster: (programId, periodStart = null) =>
    api.get(`/programs/${programId}/nomination-roster`, { params: periodStart ? { periodStart } : {} }),
  downloadNominationCsv: (programId, periodStart = null) =>
    api.get(`/programs/${programId}/nomination-roster`, {
      params: { format: 'csv', ...(periodStart ? { periodStart } : {}) },
      responseType: 'blob',
    }),
  // DEC-1097: consolidated operator "Enrollments" table — every enrollment in
  // org scope as one packet-count-annotated row set (server-side ordering:
  // review queue first, then terminal). params: { programId, status } where
  // status is an optional CSV string ('pending,active'); omit it to get every
  // status back (see programsController.getEnrollmentsTable).
  getEnrollmentsTable: (params) => api.get('/programs/enrollments-table', { params }),
  updateEnrollmentStatus: (enrollmentId, data) => api.put(`/programs/enrollments/${enrollmentId}/status`, data),
  requestDisenrollment: (enrollmentId) => api.post(`/programs/enrollments/${enrollmentId}/disenroll`),
  duplicate: (programId) => api.post(`/programs/${programId}/duplicate`),
  updateStatus: (programId, status) => api.put(`/programs/${programId}/status`, { status }),
  // DEC-511: versioning lifecycle
  getProgramByIdVersion: (id, version) => api.get(`/programs/${id}?version=${version}`),
  listVersions: (id) => api.get(`/programs/${id}/versions`),
  // DEC-1351: funds in vs obligations out, per funding entity.
  getFundingPosition: (programId) => api.get(`/programs/${programId}/funding-position`),
  createDraft: (id) => api.post(`/programs/${id}/versions`),
  publishVersion: (id, versionId) => api.post(`/programs/${id}/versions/${versionId}/publish`),
  // DEC-578: Data Authorizations — per-program requirement list
  getRequiredAuthorizations: (id) => api.get(`/programs/${id}/required-authorizations`),
  putRequiredAuthorizations: (id, requiredAuthorizations) =>
    api.put(`/programs/${id}/required-authorizations`, { requiredAuthorizations }),
};

// DEC-575/578: Data provider catalog — drives the wizard's Data Authorizations dropdown
export const dataProvidersAPI = {
  // DEC-834: pass { includeInactive: true } to surface utilities that the
  // platform knows about but hasn't activated for Green Button yet. The
  // customer-facing UtilityPicker uses this; operator/admin callers omit it
  // to keep their dropdowns to integrated providers only.
  list: (kind = null, { includeInactive = false } = {}) => api.get('/data-providers', {
    params: {
      ...(kind ? { kind } : {}),
      ...(includeInactive ? { includeInactive: 'true' } : {}),
    },
  }),
};

// Site-level utility OAuth initiation — JSON sibling of GET /green-button/authorize.
// Returns { redirectUrl } on success; structured error JSON on failure so the
// client can render a toast instead of opening a blank tab on a 503.
export const greenButtonAPI = {
  // Legacy: caller passes utility slug explicitly. Kept for back-compat
  // with any path that still resolves utility on the client (e.g. the
  // data_authorization task button reads provider from task metadata, not
  // from the site's FK).
  // PROD incident 2026-08-09: authorize is SAME-TAB now — returnTo (relative
  // path, validated server-side) is where the OAuth callback lands the user
  // afterward, with gb_success/gb_error/meter_selection params preserved for
  // the destination page's on-mount handlers.
  initiateSiteAuthorization: (utility, siteId, returnTo) =>
    api.post('/green-button/authorize/initiate', { utility, siteId, returnTo }),
  // DEC-836: server resolves utility from sites.utility_provider_id. The
  // site-level Connect button uses this — the site already knows which
  // utility it's on, no need to fetch + pick on the client.
  initiateSiteAuthorizationBySite: (siteId, returnTo) =>
    api.post('/green-button/authorize/initiate', { siteId, returnTo }),
};

// DEC-672 (B-1): Internal-admin Green Button triggers. Gated server-side by
// requireInternalOrgMember; the UI hides these buttons behind the same check
// so non-admins don't see (and 403) on click.
export const adminGreenButtonAPI = {
  discoverUsagePoints: ({ siteId, participantAuthorizationId } = {}) =>
    api.post('/admin/green-button/discover-usage-points', { siteId, participantAuthorizationId }),
  listUsagePoints: (siteId) =>
    api.get('/admin/green-button/usage-points', { params: { siteId } }),
};

// DEC-583/585: Beta program invitation management.
// All endpoints require the caller to be an active member of an
// is_internal=TRUE org (the Deco Energy team). Non-members get 403.
export const betaInvitationsAPI = {
  list: (status = 'all') => api.get('/admin/beta-invitations', { params: { status } }),
  create: ({ email, expiresInDays, note }) =>
    api.post('/admin/beta-invitations', { email, expiresInDays, note }),
  resend: (id, expiresInDays = null) =>
    api.post(`/admin/beta-invitations/${id}/resend`, expiresInDays ? { expiresInDays } : {}),
  revoke: (id) => api.post(`/admin/beta-invitations/${id}/revoke`, {}),
};

// DEC-720: Internal admin team management. Same internal-org gate as
// betaInvitationsAPI. Invite create/resend/revoke reuse organizationsAPI
// with the orgId from getOverview() — that's the seeded Deco Energy org.
export const adminTeamAPI = {
  getOverview: () => api.get('/admin/team'),
  removeMember: (userId) => api.delete(`/admin/team/members/${userId}`),
};

// DEC-982 follow-up: internal-admin operator promotion by email. Same
// internal-org gate as betaInvitationsAPI (non-members get 403). The
// deliberate path for minting operators now that self-service promotion is
// closed. userType is 'operator' | 'consumer'; the same endpoint sets a user
// back to consumer (server enforces the active-programs demotion guard).
export const adminUsersAPI = {
  setAccountType: (email, userType) =>
    api.post('/admin/users/account-type', { email, userType }),
};

// DEC-1491: re-sync a program's funding position from the Stripe payments
// balance. Same internal-org gate as the rest of /admin/funding. Preview is
// read-only; apply records the unrecorded difference as a funding receipt.
export const adminFundingAPI = {
  previewSync: (orgId) => api.post('/admin/funding/sync/preview', { orgId }),
  applySync: (orgId, { orgFundingEntityId = null, expectedDifference = null } = {}) =>
    api.post('/admin/funding/sync', { orgId, orgFundingEntityId, expectedDifference }),
};

// DEC-1031: settlement verification (the funding-source gate's write side).
// Same internal-org gate. Only settlement-verified operator/hybrid orgs can
// create payments; verify/revoke is the freeze-the-org lever.
export const adminOrganizationsAPI = {
  listSettlement: () => api.get('/admin/organizations/settlement'),
  setSettlementVerification: (orgId, verified) =>
    api.put(`/admin/organizations/${orgId}/settlement-verification`, { verified }),
};

// DEC-1031: held (self-dealing) payment review queue. Approve unlocks the
// payment for the operator to complete; reject fails it and reverses the
// wallet accrual.
export const adminPaymentReviewsAPI = {
  list: () => api.get('/admin/payments/reviews'),
  decide: (paymentId, decision, note) =>
    api.post(`/admin/payments/${paymentId}/review`, { decision, note }),
};

// Platform Analytics — per-site cohort + funnel views (internal-org gated)
export const adminAnalyticsAPI = {
  getCohort:   () => api.get('/admin/analytics/cohort'),
  getFunnel:   () => api.get('/admin/analytics/funnel'),
  getTimeline: (userId) => api.get(`/admin/analytics/timeline/${userId}`),
  // DEC-822: cross-fleet device alerts (severity-first, newest-first from the
  // server). includeIgnored also surfaces customer-dismissed rows.
  getDeviceAlerts: (includeIgnored = false) =>
    api.get(`/admin/analytics/device-alerts${includeIgnored ? '?includeIgnored=1' : ''}`),
  // DEC-1250: north-star settlement margin snapshots (persisted at compute
  // time) + the active platform-fee target.
  getSettlementMargin: () => api.get('/admin/analytics/settlement-margin'),
};

// DEC-55: Beta-gate waitlist. Public submit (no auth) + admin queue
// management (internal-org gated). The submit endpoint is reached
// pre-gate, so the BetaAccessGate component imports this directly
// rather than through the rest-of-app authenticated `api` instance.
// The shared `api` instance is fine here because the public POST
// endpoint ignores the Authorization header (none is sent for
// unauthenticated visitors anyway).
export const waitlistAPI = {
  submit: ({ email, zipCode, sourceUrl, referrer, website }) =>
    api.post('/waitlist', { email, zipCode, sourceUrl, referrer, website }),
  list: (status = 'pending') =>
    api.get('/admin/beta-invitations/waitlist', { params: { status } }),
  mint: (id) => api.post(`/admin/beta-invitations/waitlist/${id}/mint`),
  updateNotes: (id, notes) =>
    api.patch(`/admin/beta-invitations/waitlist/${id}/notes`, { notes }),
};

// DEC-588/589/590: Feature flag management.
// `getMine` is the only client-facing endpoint — the rest are gated by
// internal-org membership and consumed by the admin UI (DEC-590).
export const featureFlagsAPI = {
  getMine: () => api.get('/feature-flags/me'),
  list: (includeArchived = false) =>
    api.get('/admin/feature-flags', { params: includeArchived ? { includeArchived: true } : {} }),
  create: ({ key, description, defaultEnabled }) =>
    api.post('/admin/feature-flags', { key, description, defaultEnabled }),
  update: (id, patch) => api.patch(`/admin/feature-flags/${id}`, patch),
  listGrants: (id) => api.get(`/admin/feature-flags/${id}/grants`),
  createGrant: (id, { targetType, targetValue, enabled = true }) =>
    api.post(`/admin/feature-flags/${id}/grants`, { targetType, targetValue, enabled }),
  revokeGrant: (flagId, grantId) =>
    api.delete(`/admin/feature-flags/${flagId}/grants/${grantId}`),
};

// DEC-549/551/550/573: Tariff-to-Wizard LLM extraction
export const tariffAPI = {
  uploadDocument: (file, opts = {}) => {
    const form = new FormData();
    form.append('tariff', file);
    if (opts.programId) form.append('programId', opts.programId);
    if (opts.tariffEffectiveDate) form.append('tariffEffectiveDate', opts.tariffEffectiveDate);
    if (opts.tariffRevisionLabel) form.append('tariffRevisionLabel', opts.tariffRevisionLabel);
    if (opts.supersedesId) form.append('supersedesId', opts.supersedesId);
    return api.post('/programs/tariff-documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDocument: (id) => api.get(`/programs/tariff-documents/${id}`),
  listForProgram: (programId) => api.get(`/programs/${programId}/tariff-documents`),
  attachToProgram: (documentId, programId) =>
    api.post(`/programs/tariff-documents/${documentId}/attach`, { programId }),
  deleteDocument: (id) => api.delete(`/programs/tariff-documents/${id}`),
  // DEC-550: kick off extraction → returns { jobId, status }
  kickoffExtraction: ({ tariffDocumentId, programId, programVersionId }) =>
    api.post('/programs/tariff-extract', { tariffDocumentId, programId, programVersionId }),
  // DEC-571: poll extraction job status
  getJob: (jobId) => api.get(`/extraction-jobs/${jobId}`),
  // DEC-571: SSE stream URL — caller passes to new EventSource()
  jobStreamUrl: (jobId) => `${API_URL}/extraction-jobs/${jobId}/stream`,
  // DEC-573: read categorized templates after extraction completes
  getTemplates: (versionId) =>
    api.get(`/programs/tariff-extraction/${versionId}/templates`),
  // DEC-546: mark fields as admin-reviewed (edit or confirm-as-is)
  touchFields: (versionId, fieldPaths, { confirmAsIs = false } = {}) =>
    api.post(`/programs/tariff-extraction/${versionId}/touch`, { fieldPaths, confirmAsIs }),
  // DEC-547: diff extraction metadata between two versions (re-upload comparison)
  getDiff: (versionId, againstVersionId = null) =>
    api.get(`/programs/tariff-extraction/${versionId}/diff`, {
      params: againstVersionId ? { againstVersionId } : {},
    }),
};

// DEC-524: Baseline engine operator tooling
export const baselinesAPI = {
  getStatus: (eventId) => api.get(`/baselines/status/${eventId}`),
  recompute: (eventId, siteId) => api.post('/baselines/recompute', { eventId, siteId }),
};

// Events API
export const eventsAPI = {
  create: (data) => api.post('/events', data),
  getOperatorEvents: () => api.get('/events/operator'),
  // DEC-1299: the program's configured event types (published version) —
  // feeds the event-creation form's Event Type select so events carry the
  // SAME identity baselines/settlement were configured against, rather than
  // the old hardcoded demand_response/load_shift/test enum.
  getEventTypes: (programId) => api.get(`/events/program/${programId}/event-types`),
  getUpcoming: (siteId) => api.get(`/events/upcoming/${siteId}`),
  getHistorical: (siteId) => api.get(`/events/historical/${siteId}`),
  participate: (data) => api.post('/events/participate', data),
};

// DEC-1264: PG&E ART dispatch email operator surface — provenance ("which
// events came from an auto-ingested email, and what did the email say") plus
// the awaiting_approval queue the operator gate parks dispatches in. Every
// route is operator-role + org-scoped server-side (dispatchEmailsController);
// nothing here needs an X-Organization-Id override the way paymentsAPI does.
export const dispatchEmailsAPI = {
  // params: { status: 'awaiting_approval' } for the approval queue;
  // { status: 'parse_failed,no_program,…' } (comma list) for the DEC-1465
  // Needs Review list; omit for the full channel history (server caps at
  // 200, newest fetched_at first).
  list: (params) => api.get('/dispatch-emails', { params }),
  // DEC-1465: one ledger row by id — the Needs Review modal's fetch (a
  // quarantined row has no event to look up by). 404 when the caller's org
  // can't see it (program-less rows are internal-org only).
  get: (id) => api.get(`/dispatch-emails/${id}`),
  // 404s when the event was created manually (no dispatch email row) — the
  // "Email" provenance badge only ever renders when event.dispatch_email_id
  // is set, so callers shouldn't hit that in normal use.
  getForEvent: (eventId) => api.get(`/dispatch-emails/for-event/${eventId}`),
  // Approve actuates hardware server-side; the endpoint REQUIRES an
  // Idempotency-Key so a blind retry replays the cached response instead
  // of re-running the mutation. One key per user action.
  approve: (id) => api.post(`/dispatch-emails/${id}/approve`, {}, {
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  }),
  dismiss: (id) => api.post(`/dispatch-emails/${id}/dismiss`, {}, {
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  }),
};

// Documents API
export const documentsAPI = {
  upload: (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getBySite: (siteId) => api.get(`/documents/site/${siteId}`),
  delete: (id) => api.delete(`/documents/${id}`),
};

// DEC-1009: wallet/payout-account calls are scoped per (user, org) — the
// server 400s without an X-Organization-Id header (requireOrgContext) and
// 403s if it doesn't resolve to an active membership. Building the header
// object inline (rather than always merging `{}`) keeps a caller that
// forgets orgId from silently sending no header and getting a 400 that looks
// unrelated to the omission.
function orgHeader(orgId) {
  return orgId ? { 'X-Organization-Id': orgId } : {};
}

// Payments API
export const paymentsAPI = {
  // DEC-979: POST /payments mints ledger balance, so the route REQUIRES an
  // Idempotency-Key header (one uuid per confirmed attempt) — a retried submit
  // can never double-pay a consumer. The active org is added by the global
  // request interceptor (X-Organization-Id); the server scopes the write to it.
  create: (data, idempotencyKey) =>
    api.post('/payments', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),
  // DEC-1204: the derived settlement path. Preview is read-only and returns
  // { outcome, amount, trace }; the operator confirms THAT figure and the
  // create call passes it back as confirmedAmount (the server re-derives and
  // 409s if the number moved). Create mints ledger balance → Idempotency-Key
  // required, same as create().
  settlementPreview: (data) => api.post('/payments/settlement/preview', data),
  settlementCreate: (data, idempotencyKey) =>
    api.post('/payments/settlement', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),
  getBySite: (siteId) => api.get(`/payments/site/${siteId}`),
  updateStatus: (id, data) => api.put(`/payments/${id}/status`, data),
  // DEC-1308: the program-scoped queue of payments awaiting an operator
  // decision (pending + disputed), and the batch release.
  //
  // The batch REQUIRES an Idempotency-Key — it settles real wallet balance for
  // up to 200 consumers in one call, and a blind retry after a dropped
  // connection must replay the original result rather than re-run the loop.
  settlementQueue: (params) => api.get('/payments/settlement-queue', { params }),
  settlementQueueComplete: (paymentIds, idempotencyKey) =>
    api.post('/payments/settlement-queue/complete', { paymentIds },
      idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),
  // DEC-988: the current active platform fee rate — the ONLY sanctioned source
  // for a rate in the client (the DEC-991 payment-creation live preview reads
  // this; never hardcode or compute 7.5% in a component).
  getFeeRate: () => api.get('/payments/fee-rate'),
  // DEC-955/1009: consumer payout account (Stripe Connect) — one per (user,
  // org). orgId is REQUIRED (the mount site's own org — see the component
  // docstrings for exactly which prop each caller passes).
  getPayoutAccount: (orgId) => api.get('/payments/payout-account', { headers: orgHeader(orgId) }),
  // DEC-980: triggerSource attributes the click — 'checklist', 'reminder',
  // 'payments_tab', 'actions_center'. It rides the analytics event only; the
  // server clamps it and defaults to 'unknown', so an omitted value is safe.
  // DEC-1383: entityType is the payee's OWN answer to "individual or business",
  // asked before the redirect. It becomes business_type on the Stripe account.
  // Only meaningful on a first connection — the server discards it once an
  // account exists, because it could never reach Stripe from there.
  createPayoutConnectSession: (siteId, orgId, triggerSource, entityType) =>
    api.post(
      '/payments/payout-account/connect-session',
      { siteId, triggerSource, entityType },
      { headers: orgHeader(orgId) },
    ),
  // DEC-960: disconnect the payout bank account. Responds with the same
  // deriveStatus payoutAccount shape as GET /payments/payout-account
  // (status 'disconnected') so callers can swap state in without a refetch.
  disconnectPayoutAccount: (orgId) => api.post('/payments/payout-account/disconnect', {}, { headers: orgHeader(orgId) }),
  // DEC-999: confirm an emailed reconnect. Deliberately carries NO org header
  // and needs no session — the threat model is a compromised session, and the
  // user may follow the link on a device that has never logged in. The
  // single-use expiring token is the whole credential.
  confirmPayoutReconnect: (token) => api.post('/payments/payout-account/confirm-reconnect', { token }),
  // DEC-997/1009: the (user, org) wallet — available/pending/inTransit +
  // withdrawal eligibility. All amounts are decimal STRINGS ("128.40").
  getWallet: (orgId) => api.get('/payments/wallet', { headers: orgHeader(orgId) }),
  // DEC-1002: ledger-truth earnings rollup for the Dashboard strip —
  // totalEarned/pending/available/withdrawn/inTransit, decimal strings,
  // satisfying Total Earned = Withdrawn + Available + Pending to the cent
  // per (user, org). NOTE: `withdrawn` includes in-flight money (the only
  // definition under which the identity holds mid-withdrawal).
  getEarnings: (orgId) => api.get('/payments/earnings', { headers: orgHeader(orgId) }),
  // DEC-997: request a withdrawal. The server REQUIRES an Idempotency-Key
  // header (uuid v4, one per logical attempt) so a retried request can never
  // double-withdraw. `amount` is a decimal string.
  createWithdrawal: (amount, idempotencyKey, orgId) =>
    api.post('/payments/withdrawals', { amount }, { headers: { 'Idempotency-Key': idempotencyKey, ...orgHeader(orgId) } }),
  // DEC-997: withdrawal history (desc, ≤50) for the Payments-tab list.
  listWithdrawals: (orgId) => api.get('/payments/withdrawals', { headers: orgHeader(orgId) }),
  // DEC-1004: cross-site earnings-by-event feed for the Dashboard's Earnings
  // pane (org-wide, optionally narrowed via params.siteId). Same orgHeader
  // pattern as every other payments call; params.limit/offset are optional
  // (server defaults limit to 100, clamps to 500 max).
  getEarningsEvents: (orgId, params) => api.get('/payments/earnings/events', { params, headers: orgHeader(orgId) }),
};

// DEC-1208 (M5): settlement trace API — the payment-authoritative period walk.
// Both endpoints are flag-gated server-side (settlement_period_view, 404 when
// off) and dual-tier: site owners and operators of the program's org. period
// is 'YYYY-MM'; programId is required only when the site has more than one
// enrollment (the 400 body lists the choices).
export const settlementAPI = {
  listPeriods: (siteId) => api.get(`/sites/${siteId}/settlement-periods`),
  getPeriod: (siteId, period, programId) =>
    api.get(`/sites/${siteId}/settlement-periods/${period}`, {
      params: programId ? { programId } : undefined,
    }),
  // DEC-1270 (M6): the per-event diagnostic payload (day audit, chart series,
  // per-device contribution, marginal framing). Same flag + authz as above.
  getEventDetail: (siteId, eventId) => api.get(`/sites/${siteId}/events/${eventId}/detail`),
  // DEC-1214: engagement beacons. The client names only the allow-listed
  // step/section engaged; the server re-derives every trust-relevant property
  // and is the sole analytics writer. Fire-and-forget — callers swallow errors.
  sendPeriodEngagement: (siteId, period, steps, programId) =>
    api.post(
      `/sites/${siteId}/settlement-periods/${period}/engagement`,
      { steps },
      { params: programId ? { programId } : undefined }
    ),
  sendEventEngagement: (siteId, eventId, sections) =>
    api.post(`/sites/${siteId}/events/${eventId}/engagement`, { sections }),
};

// Performance API
//
// DEC-799: Canonical performance endpoints (single source of truth) live on
// /api/sites and /api/users — getSitePerformance / getUserPerformance below.
// These are what the Performance tab, site card, and dashboard rollup all
// consume. By construction, no two surfaces can drift.
//
// The legacy `/performance/rating/:siteId` + `/performance/history/:siteId`
// endpoints remain for back-compat (operator-side surfaces) but new consumer
// UIs should NOT use them.
export const performanceAPI = {
  // Canonical — use these
  getSitePerformance: (siteId) => api.get(`/sites/${siteId}/performance`),
  // orgId scopes the rollup to a single org (matching the sites list the
  // dashboard already shows). Omit to aggregate across all the user's sites.
  getUserPerformance: (userId, orgId = null) =>
    api.get(`/users/${userId}/performance${orgId ? `?orgId=${orgId}` : ''}`),
  // DEC-1164/1167: the automatic, cross-program, trailing-90-day site score.
  // { score, provisional, eventsCounted, eventsRequired, windowDays } — score
  // is null while provisional=true (fewer than eventsRequired measured events).
  // Never fall back to a fabricated 100 when this is provisional — render the
  // honest cold-start "Provisional — N of eventsRequired events" state.
  getGeneralScore: (siteId) => api.get(`/sites/${siteId}/general-score`),

  // Legacy — operator-side / pre-DEC-799 surfaces only
  // (DEC-1129: `record` removed with its endpoint — nothing ever called it.)
  getRating: (siteId) => api.get(`/performance/rating/${siteId}`),
  getHistory: (siteId) => api.get(`/performance/history/${siteId}`),
};

// DEC-1167: consumer-facing per-enrollment capability + confidence + recent
// change history. Owner-only (404 for anyone else) — same as every other
// site-scoped read below.
export const capabilityAPI = {
  getBySite: (siteId) => api.get(`/sites/${siteId}/capability`),
};

// DEC-1247: nomination-window facts per enrollment (deadline, assessed state,
// current-period submission) — drives the missed-window notice. An empty
// `enrollments` array means the program(s) run no nomination cycle: render
// nothing, not an empty state.
export const nominationWindowAPI = {
  getBySite: (siteId) => api.get(`/sites/${siteId}/nomination-window`),
};

// DEC-1268: utility-sourced site characteristics (account/meter/service
// category/territory) with provenance. Owner-only; full (unmasked) values —
// the owner is the account holder. Keys absent from the payload mean the
// utility hasn't sourced that fact yet — omit the row, never show a dash.
export const utilityAttributesAPI = {
  getBySite: (siteId) => api.get(`/sites/${siteId}/utility-attributes`),
};

// DEC-733: utility-connection health endpoint. Returns the classified
// failure-mode state for the site's most recent utility_data_share
// authorization — same shape consumed by the Site Details banner and
// the Dashboard tile pill so they can't drift.
export const utilityConnectionAPI = {
  getStatus: (siteId) => api.get(`/sites/${siteId}/utility-connection`),
};

// DEC-730: meter-selection modal. listOptions returns unassigned UPs +
// per-option address-match flag + the site's own address (for the
// modal's header line). assign + ignore are the two write paths from
// the modal CTAs.
export const usagePointAssignmentAPI = {
  listOptions: (siteId) => api.get(`/sites/${siteId}/usage-point-options`),
  assign: (siteId, usagePointIds) =>
    api.post(`/sites/${siteId}/usage-point-assignment`, { usagePointIds }),
  ignore: (siteId, usagePointIds) =>
    api.post(`/sites/${siteId}/usage-point-ignore`, { usagePointIds }),
};

// Matching API
export const matchingAPI = {
  getMatchedPrograms: (siteId) => api.get(`/matching/site/${siteId}`),
  quickEnroll: (data) => api.post('/matching/enroll', data),
  getEarningsEstimate: (programId, siteId) => api.get(`/matching/earnings/${programId}/${siteId}`),
  checkEligibility: (siteId, programId) => api.get(`/matching/check-eligibility/${siteId}/${programId}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
};

// Device Catalog API
export const deviceCatalogAPI = {
  getCatalog: (deviceType) => api.get(`/device-catalog/type/${deviceType}`),
  getManufacturers: (deviceType, opts = {}) => api.get(`/device-catalog/type/${deviceType}/manufacturers${opts.includeNonConnectable ? '?includeNonConnectable=1' : ''}`),
  getModels: (deviceType, manufacturer) => api.get(`/device-catalog/type/${deviceType}/manufacturer/${encodeURIComponent(manufacturer)}/models`),
  getSpecifications: (catalogId) => api.get(`/device-catalog/${catalogId}/specifications`),
};

// Tasks API
export const tasksAPI = {
  getBySite: (siteId) => api.get(`/sites/${siteId}/tasks`),
  create: (siteId, data) => api.post(`/sites/${siteId}/tasks`, data),
  getById: (taskId) => api.get(`/tasks/${taskId}`),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
  // DEC-312: Generic task completion — sets status='completed' server-side
  complete: (taskId, metadata) => api.post(`/tasks/${taskId}/complete`, { metadata }),
  // DEC-314: Agreement-specific acceptance — creates agreement_acceptances record server-side
  acceptAgreement: (taskId, signature) => api.post(`/tasks/${taskId}/accept-agreement`, { signature }),
  // DEC-579: Mint a state-bound OAuth URL for a data_authorization task. The server
  // returns { redirectUrl, state }; the caller navigates the customer's browser to
  // redirectUrl, the utility callbacks /api/green-button/callback, and the callback
  // handler marks this task completed via state.task_id.
  initiateDataAuthorization: (taskId, returnTo) => api.post(`/tasks/${taskId}/data-authorization/initiate`, { returnTo }),
};

// Upload Links API
export const uploadLinksAPI = {
  generate: (siteId, data) => api.post(`/sites/${siteId}/upload-links`, data),
  getBySite: (siteId) => api.get(`/sites/${siteId}/upload-links`),
  revoke: (tokenId) => api.delete(`/upload-links/${tokenId}`),
  validate: (token) => api.get(`/upload/${token}`),
  upload: (token, formData) => api.post(`/upload/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Onboarding API (DEC-379)
// Each method maps to a server endpoint under /api/sites/:siteId/onboarding/*.
// The wizard page (SiteSetup.js) is the sole consumer of this API object.
export const onboardingAPI = {
  start:           (siteId)       => api.post(`/sites/${siteId}/onboarding/start`),
  completeUtility: (siteId, data) => api.put(`/sites/${siteId}/onboarding/utility`, data),
  completeDevices: (siteId)       => api.put(`/sites/${siteId}/onboarding/devices`),
  startTest:       (siteId)       => api.post(`/sites/${siteId}/onboarding/test-devices`),
  getTestStatus:   (siteId)       => api.get(`/sites/${siteId}/onboarding/test-status`),
  confirm:         (siteId)       => api.put(`/sites/${siteId}/onboarding/confirm`),
  // DEC-1169: completion for the 2-step wizard. `confirm` above belongs to the
  // retired Site Capacity step and additionally auto-satisfied the capability
  // task from a simulated test; this one only marks setup done.
  completeSetup:   (siteId)       => api.put(`/sites/${siteId}/onboarding/complete`),
  getStatus:       (siteId)       => api.get(`/sites/${siteId}/onboarding/status`),
};

// DEC-1160/1161: the real capability verification test. Enrollment-scoped
// because capability is (DEC-1154). `start` REQUIRES consentAcknowledged and a
// client-minted idempotencyToken — the server rejects a request without either,
// since both guard against actuating someone's hardware unintentionally.
export const verificationTestAPI = {
  start:     (enrollmentId, body)   => api.post(`/enrollments/${enrollmentId}/verification-test`, body),
  abort:     (enrollmentId, testId) => api.post(`/enrollments/${enrollmentId}/verification-test/${testId}/abort`),
  getLatest: (enrollmentId)         => api.get(`/enrollments/${enrollmentId}/verification-test`),
  // DEC-1271 (M6): the test's results as the source-agnostic per-event
  // payload — rendered by the same EventDetail components real events use.
  getDetail: (enrollmentId, testId) => api.get(`/enrollments/${enrollmentId}/verification-test/${testId}/detail`),
};

// Device Controls API (Texture is the first provider; future providers plug
// in alongside under their own /device-controls/<slug>/* sub-namespace).
// The JS export name is kept as `textureAPI` for now to limit caller churn;
// a follow-up PR will rename to `deviceControlsAPI` with provider sub-keys.
//
// DEC-805 deleted the legacy `.connect(deviceId)` method that fronted a
// non-existent Texture OAuth endpoint. Callers should use `.connectHeadless`
// only.
export const textureAPI = {
  // DEC-1062: connectSessionId is the client-minted funnel-analytics
  // correlation id (see services/analytics.js#newConnectSessionId), threaded
  // through so the server can persist it on the pending-state row and attach
  // it to its own discovery/bind events. Optional/additive — omitting it
  // just means those server events won't join this client's stream.
  // DEC-1074: provider-agnostic connect paths (provider resolved server-side
  // from the device, never named in the URL). The server keeps the old
  // /texture/* paths as aliases for one deploy cycle; new calls use /connect/*.
  connectHeadless:  (deviceId, connectSessionId) =>
    api.post(`/device-controls/connect/${deviceId}`, connectSessionId ? { connectSessionId } : {}),
  confirmPopup:     (data)     => api.post('/device-controls/connect/confirm', data),
  // DEC-1141: `finalize` removed — no caller; the post-popup path is discover + bind.
  // DEC-1055/1059: provider-agnostic discovery + bind (provider resolved from the
  // device, not the URL). discover → { state: 'bound'|'needs_selection'|'still_syncing', candidates?, ... };
  // bind({ state, selections:[{ providerDeviceId, decoDeviceType?, deviceName? }] }).
  discover:         (deviceId, data) => api.post(`/device-controls/discover/${deviceId}`, data),
  bind:             (data)     => api.post('/device-controls/bind', data),
  getDeviceStatus:  (deviceId) => api.get(`/device-controls/status/${deviceId}`),
  getSiteStatus:    (siteId)   => api.get(`/device-controls/site/${siteId}/status`),
  disconnect:       (deviceId) => api.post(`/device-controls/disconnect/${deviceId}`),
  // DEC-822: dismiss a non-critical active device alert (server flips it to
  // IGNORED). 422 ALERT_NOT_DISMISSIBLE for CRITICAL alerts — callers should
  // never expose this action for those (DeviceAlertBadge already gates the
  // dismiss affordance on `alert.dismissible`).
  dismissAlert:     (alertId) => api.post(`/device-controls/alerts/${alertId}/dismiss`),
};

// Embed / Option-B break-out API (DEC-916 / DEC-922, hardened DEC-926)
//
// The embedded EnrollmentBlock widget posts { programId, email } to
// /api/embed/enroll, which emails a 6-digit code and returns a continuePath
// (/enroll/continue?program=<id>&email=<email>) — NO session material. The
// break-out page collects the code and calls verify() below; the server issues a
// real first-party session JWT ONLY after the OTP proves email ownership
// (DEC-926). Public, credential-less — no Authorization header required.
export const embedAPI = {
  startEnroll: (programId, email) => api.post('/embed/enroll', { programId, email }),
  // attribution = { utilitySlug, sessionId } — DEC-923 funnel stitching.
  verify: (programId, email, code, attribution = {}) =>
    api.post('/embed/verify', { programId, email, code, ...attribution }),
};

// Site Access API
export const siteAccessAPI = {
  getAccess: (siteId) => api.get(`/sites/${siteId}/access`),
  getCollaborators: (siteId) => api.get(`/sites/${siteId}/collaborators`),
  getServiceProviders: (siteId) => api.get(`/sites/${siteId}/service-providers`),
  getMyRoles: (siteId) => api.get(`/sites/${siteId}/my-roles`),
  invite: (siteId, data) => api.post(`/sites/${siteId}/access/invite`, data),
  revoke: (siteId, userId, accessLevel) =>
    api.delete(`/sites/${siteId}/access/${userId}${accessLevel ? `?accessLevel=${accessLevel}` : ''}`),
  cancelInvitation: (siteId, invitationId) => api.delete(`/sites/${siteId}/invitations/${invitationId}`),
};

// Collaborator API (for users with roles on other people's sites)
export const collaboratorAPI = {
  getSites: () => api.get('/collaborator/sites'),
};

// Users API
// DEC-396: onboarding-state endpoints used by the zero-site Dashboard checklist.
// The enrollment agent is building GET /api/users/onboarding-state and
// PATCH /api/users/onboarding-state. Calls fail gracefully in the consumer —
// the checklist renders a working default state if the endpoint 404s.
export const usersAPI = {
  getOnboardingState: () => api.get('/users/onboarding-state'),
  updateOnboardingState: (data) => api.patch('/users/onboarding-state', data),
  // DEC-989: one-time first-earnings fee-explainer banner (same contract
  // shape; dedicated users column so it can fire long after onboarding).
  getEarningsExplainerState: () => api.get('/users/earnings-explainer-state'),
  dismissEarningsExplainer: () => api.patch('/users/earnings-explainer-state', { dismissed: true }),
};

// Site Inbox API
export const siteInboxAPI = {
  getMessages: (siteId, params = {}) => api.get(`/sites/${siteId}/messages`, { params }),
  sendMessage: (siteId, data) => api.post(`/sites/${siteId}/messages`, data),
  markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),
  getUnreadCount: (siteId, params = {}) => siteId
    ? api.get(`/sites/${siteId}/messages/unread-count`, { params })
    : api.get('/messages/unread-count'),
  // DEC-653: enrollment-scoped helpers
  getEnrollmentMessages: (siteId, enrollmentId) =>
    api.get(`/sites/${siteId}/messages`, { params: { enrollmentId } }),
  sendEnrollmentMessage: (siteId, enrollmentId, body) =>
    api.post(`/sites/${siteId}/messages`, { enrollmentId, body }),
};

// Service Providers API (for service provider users)
export const serviceProvidersAPI = {
  getDashboard: () => api.get('/service-providers/dashboard'),
  getSites: () => api.get('/service-providers/sites'),
  getSiteDetails: (siteId) => api.get(`/service-providers/sites/${siteId}`),
  getTasks: (params) => api.get('/service-providers/tasks', { params }),
  updateTask: (taskId, data) => api.put(`/service-providers/tasks/${taskId}`, data),
};

// DEC-540: Org Funding Entities API
export const fundingEntitiesAPI = {
  list: (orgId) => api.get(`/organizations/${orgId}/funding-entities`),
  create: (orgId, data) => api.post(`/organizations/${orgId}/funding-entities`, data),
  update: (orgId, entityId, data) => api.put(`/organizations/${orgId}/funding-entities/${entityId}`, data),
  delete: (orgId, entityId) => api.delete(`/organizations/${orgId}/funding-entities/${entityId}`),
};

// Organizations API (DEC-442)
export const organizationsAPI = {
  create: (data) => api.post('/organizations', data),
  getAll: () => api.get('/organizations'),
  getById: (orgId) => api.get(`/organizations/${orgId}`),
  update: (orgId, data) => api.put(`/organizations/${orgId}`, data),
  inviteMember: (orgId, data) => api.post(`/organizations/${orgId}/members`, data),
  removeMember: (orgId, userId) => api.delete(`/organizations/${orgId}/members/${userId}`),
  assignSite: (orgId, siteId) => api.post(`/organizations/${orgId}/sites`, { siteId }),
  // DEC-715
  matchByDomain: (email) => api.get(`/organizations/match-domain?email=${encodeURIComponent(email)}`),
  search: (query) => api.get(`/organizations/search?q=${encodeURIComponent(query)}`),
  createJoinRequest: (orgId, data) => api.post(`/organizations/${orgId}/join-requests`, data || {}),
  myJoinRequests: (status = 'pending') => api.get(`/organizations/my/join-requests?status=${encodeURIComponent(status)}`),
  // DEC-718
  listOrgJoinRequests: (orgId, status = 'pending') =>
    api.get(`/organizations/${orgId}/join-requests?status=${encodeURIComponent(status)}`),
  approveJoinRequest: (orgId, requestId) =>
    api.post(`/organizations/${orgId}/join-requests/${requestId}/approve`),
  rejectJoinRequest: (orgId, requestId) =>
    api.post(`/organizations/${orgId}/join-requests/${requestId}/reject`),
  // DEC-716: org invitation tokens
  createInvitation: (orgId, data) => api.post(`/organizations/${orgId}/invitations`, data),
  listInvitations: (orgId, status = 'pending') =>
    api.get(`/organizations/${orgId}/invitations?status=${encodeURIComponent(status)}`),
  resendInvitation: (orgId, invitationId) =>
    api.post(`/organizations/${orgId}/invitations/${invitationId}/resend`),
  revokeInvitation: (orgId, invitationId) =>
    api.post(`/organizations/${orgId}/invitations/${invitationId}/revoke`),
  lookupInvitation: (token) =>
    api.get(`/organizations/invitations/lookup?token=${encodeURIComponent(token)}`),
  acceptInvitation: (token) =>
    api.post(`/organizations/invitations/accept`, { token }),
};

export default api;
